import { useEffect, useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { toStatus, type ApiItem } from "../../utils/applicationCards";
import { DEBRIEF_DAYS, detectOaKind, finishedOn, isCodingOa, leftOaOn, oaEvents, type Debrief, type PrepEventDoc } from "../../utils/prepEvents";
import { takeDay } from "../../utils/prepPlanner";
import { daysBetween } from "../../utils/prepSkills";
import { topicName } from "../../data/prep/topics";
import { localToday } from "../../utils/usePrepPlan";
import { OA_COLOR } from "./PrepOaCard";
import { DebriefDialog } from "./PrepDebrief";
import { saveDebrief } from "../../utils/oaDebrief";

// On a card's sheet in Applications, for an OA: before it, how long until it
// with a link to its prep (and, if they chose to prep themselves on the Prep page, the way back). After it
// (an "assessment completed" email, its date passed, or the card moved on): "OA done"
// with a Debrief button, or what was said in it.

const WENT_LABEL: Record<Debrief["went"], string> = { well: "Went well", ok: "Went OK", rough: "Was rough" };

export default function PrepOaRow({ card }: { card: ApiItem }) {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [saved, setSaved] = useState<PrepEventDoc | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, "users", user.uid, "prepEvents", card.id),
      (snap) => setSaved((snap.data() as PrepEventDoc | undefined) ?? {}),
      (e) => console.error("Could not load OA prep:", e)
    );
  }, [user, card.id]);

  const atOa = toStatus(card.predicted_label) === "oa";
  const leftOn = atOa ? null : leftOaOn(card);
  if (!user || !saved || (!atOa && !leftOn)) return null;
  // Not a coding test (a form, a business role's assessment): nothing to prep or debrief
  if (!isCodingOa(card) && !saved.debrief) return null;
  const save = (patch: PrepEventDoc) =>
    setDoc(doc(db, "users", user.uid, "prepEvents", card.id), patch, { merge: true }).catch((e) =>
      console.error("Could not save OA prep:", e)
    );
  const link = "font-medium text-melt-text1 underline decoration-melt-text3/40 underline-offset-2 hover:decoration-melt-text1";
  const box = "mt-3 rounded-xl bg-[#F6E3D3]/60 px-3 py-2 text-[13px]";
  const today = localToday();

  // When it's over: it was finished, the card moved on, or the day to take it has passed
  const finished = atOa ? finishedOn(card) : null;
  const [event] = atOa ? oaEvents([card], { [card.id]: { ...saved, selfPrep: false } }) : [];
  const left = event ? daysBetween(today, takeDay(event, today)) : -1;
  const over = !atOa || Boolean(finished) || left < 0;

  if (over) {
    const { debrief } = saved;
    const pending = !debrief && !saved.debriefed;
    // Long gone from OA and never debriefed: too late to ask
    const doneOn = finished ?? leftOn;
    if (pending && doneOn && daysBetween(doneOn, today) > DEBRIEF_DAYS) return null;
    const dialog = (trigger: ReactNode) => (
      <DebriefDialog
        open={asking}
        onOpenChange={setAsking}
        trigger={trigger}
        debrief={{
          company: card.company,
          kind: saved.kind ?? detectOaKind(card),
          initial: debrief,
          today,
          onSave: (answers) => {
            saveDebrief(user.uid, card, answers, saved).catch((e) => console.error("Could not save the debrief:", e));
          },
        }}
      />
    );
    if (pending) {
      return (
        <div className={`${box} flex flex-wrap items-center gap-x-3 gap-y-1`}>
          <span className="flex items-center gap-2 font-medium text-[#7A3E1D]">
            <Check size={14} strokeWidth={2.25} aria-hidden="true" />
            OA done
          </span>
          {dialog(
            <button type="button" className={link}>
              Debrief
            </button>
          )}
        </div>
      );
    }
    const stuck = debrief?.topics.map(topicName).join(", ");
    return (
      <p className={`${box} flex flex-wrap items-center gap-x-2 text-melt-text2`}>
        <span className="font-medium text-[#7A3E1D]">OA debrief:</span>
        {debrief ? (
          <>
            {WENT_LABEL[debrief.went]}
            {debrief.levels !== undefined && ` · ${debrief.levels} of 4 levels`}
            {stuck && ` · stuck on ${stuck}`}
            {debrief.questions?.length ? ` · ${debrief.questions.length} ${debrief.questions.length === 1 ? "question" : "questions"} saved` : ""}
          </>
        ) : (
          "done"
        )}
        {dialog(
          <button type="button" className={`${link} ml-auto`}>
            Edit
          </button>
        )}
      </p>
    );
  }

  if (saved.selfPrep) {
    return (
      <p className="mt-3 text-[13px] text-melt-text2">
        You’re prepping for this one yourself.{" "}
        <button type="button" className={link} onClick={() => save({ selfPrep: false })}>
          Plan prep for it
        </button>
      </p>
    );
  }

  if (!event) return null;
  const when = left > 1 ? `${left} days left` : left === 1 ? "OA tomorrow" : "OA today";

  return (
    <div className={`${box} flex flex-wrap items-center gap-x-3 gap-y-1`}>
      <span className="flex items-center gap-2 font-medium text-[#7A3E1D]">
        <i className="h-2 w-2 rounded-[2px]" style={{ background: OA_COLOR.dot }} aria-hidden="true" />
        Prep: {when}
      </span>
      <button type="button" className={link} onClick={() => navigate("/dashboard/prep")}>
        Open prep
      </button>
    </div>
  );
}
